import { Component, OnInit, Input } from '@angular/core';
import { StringValueAccessor, CreateStringValueAccessorProvider } from '../value-accessors/string-value-accessor';
import { CronBuilderOptionsEnum } from './cron-builder-options.enum';
import { UtilService } from '../../util/util.service';

@Component({
  selector: 'wl-cron-builder',
  templateUrl: 'cron-builder.component.html',
  providers: [CreateStringValueAccessorProvider(CronBuilderComponent)],
    standalone: false
})

/*
    A component to build cron strings. simple and scaleable
*/
export class CronBuilderComponent extends StringValueAccessor {
  public cronBuilderOptions = CronBuilderOptionsEnum;
  public periodOption: CronBuilderOptionsEnum;
  // day of month option is string because it can be a number or 'L'
  public dayOfMonthOption: string;
  public dayOfWeekOption: number;
  public dayOfWeekInMonthOption: string;
  public timeOption: string;
  public useTime: boolean;
  public availableTimes: Array<any>;
  public now: Date = new Date();

  writeValue(value: string) {
    super.writeValue(value);
    if (value) {
      value = this._utilService.convertUtcCronExpressionToLocal(value);
      super.writeValue(value);
      this.updateOptions();
    }
  }

  public constructor(private _utilService: UtilService) {
    super();
    this.cronBuilderOptions = CronBuilderOptionsEnum;
    this.periodOption = CronBuilderOptionsEnum.Daily;
    this.dayOfMonthOption = '1';
    this.dayOfWeekOption = 1;
    this.dayOfWeekInMonthOption = '1#1';
    this.useTime = false;
    this.timeOption = '0 0';
    this.availableTimes = new Array(96);
    this.availableTimes = this.availableTimes.fill(0).map((x, i) => ({
      value: ((i * 15) % 60) + ' ' + Math.floor(i / 4).toString(),
      timeValue: new Date(0, 1, 1, Math.floor(i / 4), (i * 15) % 60, 0),
    }));
  }

  public handlePeriodOptionChange(newPeriod: number): void {
    this.value = this.generateCronExpression();
  }

  public handleDayOfMonthOptionChange(newMonth: string): void {
    this.value = this.generateCronExpression();
  }

  public handleDayOfWeekOfMonthOptionChange(newMonth: string): void {
    this.value = this.generateCronExpression();
  }

  public handleDayOfWeekOptionChange(newMonth: number): void {
    this.value = this.generateCronExpression();
  }

  public handleHourOptionChange(useTime: string): void {
    this.value = this.generateCronExpression();
  }

  /*
        generates a cron expression based on member variables
    */
  public generateCronExpression(): string {
    let timeSection = '0 ' + this.timeOption + ' ';

    if (this.periodOption == CronBuilderOptionsEnum.Daily) return timeSection + '1/1 * ? *';
    if (this.periodOption == CronBuilderOptionsEnum.Weekly) return timeSection + '? * ' + this.dayOfWeekOption.toString() + ' *';
    if (this.periodOption == CronBuilderOptionsEnum.MonthlyByDate) return timeSection + this.dayOfMonthOption.toString() + ' 1/1 ? *';
    if (this.periodOption == CronBuilderOptionsEnum.MonthlyByDayOfTheWeek)
      return timeSection + '? 1/1 ' + this.dayOfWeekInMonthOption.toString() + ' *';

    return 'what?';
  }

  /*
        updates the options that drive the UI based on the value string
    */
  updateOptions = (): void => {
    let sections = this.value.split(' ');
    let minuteSection = sections[1];
    let hourSection = sections[2];
    let daySection = sections[3];
    let monthSection = sections[4];
    let dayOfWeekSection = sections[5];

    // these are all simple enough not to need a regex.
    this.timeOption = minuteSection + ' ' + hourSection;

    if (daySection == '1/1') {
      this.periodOption = CronBuilderOptionsEnum.Daily;
    } //"0 30 1 20 1/1 ? "
    if (daySection == '?' && monthSection == '*') {
      this.periodOption = CronBuilderOptionsEnum.Weekly;
      this.dayOfWeekOption = +dayOfWeekSection;
    }
    if (this.value.trim().endsWith(' 1/1 ? *')) {
      this.periodOption = CronBuilderOptionsEnum.MonthlyByDate;
      this.dayOfMonthOption = daySection;
    }

    //0 15 2 ? 1/1 3#2 *  => At 02:15:00am, on the 2nd Tuesday of the month, every month starting in January
    //0 15 2 ? 1/1 2L *   => At 02:15:00am, the last Monday of the month, every month starting in January
    if (this.value.trim().indexOf('#') >= 0 || this.value.trim().endsWith('L *')) {
      this.periodOption = CronBuilderOptionsEnum.MonthlyByDayOfTheWeek;
      this.dayOfWeekInMonthOption = dayOfWeekSection;
    }
  };
  public convertLocalCronExpressionToUtc(): string {
    const timeArray = this.timeOption.split(' ');
    const localMinute = parseInt(timeArray[0]);
    const localHour = parseInt(timeArray[1]);

    const now = new Date();
    const localDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), localHour, localMinute, 0, 0);

    const utcDate = localDate.getUTCDate();
    const utcHour = localDate.getUTCHours();
    const utcMinute = localDate.getUTCMinutes();

    const utcTimeSection = `0 ${utcMinute} ${utcHour}`;

    let dayDifference = utcDate - localDate.getDate();

    if (this.periodOption == CronBuilderOptionsEnum.Daily) {
      return `${utcTimeSection} 1/1 * ? *`;
    }

    if (this.periodOption == CronBuilderOptionsEnum.Weekly) {
      let dayOfWeek = this.dayOfWeekOption;
      if (dayDifference !== 0) {
        dayOfWeek = dayOfWeek + dayDifference;
        if (dayOfWeek < 1) {
          dayOfWeek = 7;
        }
        if (dayOfWeek > 7) {
          dayOfWeek = 1;
        }
      }
      return `${utcTimeSection} ? * ${dayOfWeek} *`;
    }

    if (this.periodOption == CronBuilderOptionsEnum.MonthlyByDate) {
      let dayOfMonth = this.dayOfMonthOption;
      if (dayDifference != 0) {
        if (dayOfMonth == '1' && dayDifference == -1) {
          dayOfMonth = 'L';
        } else if (dayOfMonth == 'L' && dayDifference == 1) {
          dayOfMonth = '1';
        } else if (!isNaN(parseInt(dayOfMonth))) {
          let dayNum = parseInt(dayOfMonth) + dayDifference;
          if (dayNum < 1) {
            dayNum = 1;
          }
          dayOfMonth = dayNum.toString();
        }
      }
      return `${utcTimeSection} ${dayOfMonth} 1/1 ? *`;
    }

    if (this.periodOption == CronBuilderOptionsEnum.MonthlyByDayOfTheWeek) {
      let dayOfWeekInMonth = this.dayOfWeekInMonthOption;
      if (dayDifference !== 0) {
        const match = dayOfWeekInMonth.match(/(\d)([#L])(\d)?/);
        if (match) {
          let dayOfWeek = parseInt(match[1]);
          dayOfWeek = dayOfWeek + dayDifference;
          if (dayOfWeek < 1) {
            dayOfWeek = 7;
          }
          if (dayOfWeek > 7) {
            dayOfWeek = 1;
          }
          const modifier = match[2];
          const weekNumber = match[3];

          dayOfWeekInMonth = weekNumber ? `${dayOfWeek}${modifier}${weekNumber}` : `${dayOfWeek}${modifier}`;
        }
      }
      return `${utcTimeSection} ? 1/1 ${dayOfWeekInMonth} *`;
    }

    return this.value;
  }
}
